import styled from '@emotion/styled';
import { tokens, Button, IconButton } from '@hotjar/hotsauce';
import { Accordion } from '@hotjar/getting-started-guide';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { useTranslation } from '@hotjar/translations';
import type { WizardProps } from '../Wizard/Wizard';
import { useGettingStartedSideBar } from '../../hooks/useGettingStartedSideBar';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media (min-width: 1400px) {
    padding-top: ${tokens.spacer.x2};
    flex-grow: 1;

    & ${Accordion} {
      flex-grow: 1;
    }
  }

  & ${Accordion} button[data-reach-accordion-button] {
    padding: 0;

    > svg {
      margin-right: ${tokens.spacer.x1pt5};
    }

    > div {
      padding: 0;
    }
  }
`;

const Banner = styled.div`
  height: 96px;
  background-position: center;
  background-size: cover;
  transition: background-image 500ms ease;
  margin-bottom: ${tokens.spacer.x2};

  @media (min-width: 1400px) {
    height: 120px;
    margin-bottom: ${tokens.spacer.x2pt5};
  }
`;

const Header = styled.h2`
  ${tokens.text('header5')};
  font-size: 18px;
  line-height: 20px;
  margin-bottom: ${tokens.spacer.x2};
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: -5px;
  right: 0;
`;

const StyledButton = styled(Button)`
  alignself: 'center';
  margintop: ${tokens.spacer.x2};
`;

export const SideBarWizard = ({
  banner,
  sectionToShow,
  onToggleSection,
  children,
  onDismiss,
}: WizardProps): JSX.Element => {
  const { t } = useTranslation();
  const { installed } = useTrackingCodeState();
  const { toggleSideBar } = useGettingStartedSideBar();

  const handleOnCollapse = () => {
    toggleSideBar();
  };

  return (
    <Wrapper>
      <StyledIconButton
        icon="close"
        kind="ghost"
        size="s"
        title={t('navigation:SideBar.collapse')}
        tooltipText={t('navigation:SideBar.collapse')}
        onClick={handleOnCollapse}
      />
      <Header>{t('activation:getting-started.wizard-title')}</Header>
      {banner && (
        <Banner
          aria-hidden
          data-testid="get-started-section-banner"
          style={{ backgroundImage: `url('${banner}')` }}
        />
      )}
      <Accordion sectionToShow={sectionToShow} onChange={onToggleSection} setUncontrolled>
        {children}
      </Accordion>
      {installed ? (
        <StyledButton
          css={{ alignSelf: 'center', marginTop: tokens.spacer.x2 }}
          onClick={onDismiss}
          kind="ghost"
        >
          {t('activation:getting-started.wizard-skip')}
        </StyledButton>
      ) : null}
    </Wrapper>
  );
};
